import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import images from '../../assets/images';
import axios from 'axios';
import { logout, updateUserProfile, getUserProfile } from '../../services/authService';
import { REACT_APP_APIURL } from '../../global/Environment';

import Loading from '../../components/Loading/Loading';
import ProfileDropDown from '../../components/ProfileDropDown/ProfileDropDown';
import HorseItem from './HorseItem';
import Notes from './Notes';
import RaceDateDropDowns from './RaceDateDropDowns';

import { trackActivity } from '../../services/analyticsService';
import languages from '../../data/languages';
import saddles from '../../data/saddles';
import { isStirng } from '../../global/Helpers'; // Note: typo exists in Helpers file

class Dashboard extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    this.state = {
      raceData: null,
      sampleUser: null,
      selectedTrack: '',
      trackDates: [],
      selectedRaceDate: undefined,
      trackDateRaces: [],
      selectedRace: {},
      horses: [],
      selectedHorse: undefined,
      selectedNote: undefined,
      prevHorse: undefined,
      nextHorse: undefined,
      horseIndex: 0,
      showRightPanel: true,
      error: null,
      isLoadingData: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    trackActivity('navigation: dashboard');
    this.loadData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadData = async () => {
    if (!this._isMounted) return;
    this.setState({ isLoadingData: true, error: null });

    try {
      // Get user profile
      let user;
      if (this.props.getUserProfileOverRide) {
        user = await this.props.getUserProfileOverRide();
        if (this._isMounted) this.setState({ sampleUser: user });
      } else {
        user = await this.props.getUserProfile();
      }

      if (!this._isMounted) return;

      if (!user) {
        throw new Error('Failed to load user profile');
      }

      // Get race data
      let raceData;
      if (this.props.getRaceDataOverRide) {
        raceData = await this.props.getRaceDataOverRide(user.language);
      } else {
        const response = await axios.get(`${REACT_APP_APIURL}/race/?lang=${user.language}`);
        raceData = response.data.data;
      }

      if (!this._isMounted) return;

      if (!raceData?.[0]) {
        // No purchased races/packages — show an empty state here instead of
        // redirecting to /pricing (a silent redirect reads as a bug to the user).
        this.setState({ raceData: [] });
        return;
      }

      this.setState({ raceData }, () => this.setTopLevelState());
    } catch (error) {
      console.error('Error loading data:', error);
      if (this._isMounted) {
        this.setState({ error: error.message || 'An unexpected error occurred' });
      }
    } finally {
      if (this._isMounted) {
        this.setState({ isLoadingData: false });
      }
    }
  };

  // Set initial state from race data
  setTopLevelState = (idx = 0) => {
    const { raceData } = this.state;
    
    if (!raceData?.[idx]) {
      return;
    }

    const { track, dates } = raceData[idx];
    const firstDate = dates?.[0];
    const races = firstDate?.races || [];
    const firstRace = races[0];
    const horseState = this.getFirstHorseState(firstRace?.horses || []);

    this.setState({
      selectedTrack: track,
      trackDates: dates,
      selectedRaceDate: firstDate?.date,
      trackDateRaces: races,
      selectedRace: firstRace || {},
      ...horseState,
    }, () => {
      // For demo dashboard - highlight featured race
      if (this.props.getRaceIdxToHighlight) {
        const featuredRaceIdx = this.props.getRaceIdxToHighlight();
        this.setRaceNumbersList(featuredRaceIdx);
      }
    });

    this.trackAnalyticsActions('track selected', {
      track: track,
      date: firstDate?.date,
      race: firstRace?.race_number,
      horse: horseState.selectedHorse?.name,
    });
  };

  // Handle date selection
  setTrackDatesList = (idx = 0) => {
    if (typeof idx === 'undefined') {
      return;
    }

    const { trackDates } = this.state;
    const selectedDate = trackDates[idx];
    
    if (!selectedDate) {
      return;
    }

    const races = selectedDate.races || [];
    const firstRace = races[0] || {};
    const horseState = this.getFirstHorseState(firstRace.horses || []);

    this.setState({
      selectedRaceDate: selectedDate.date,
      trackDateRaces: races,
      selectedRace: firstRace,
      ...horseState,
    });

    this.trackAnalyticsActions('date selected', {
      date: selectedDate.date,
      race: firstRace.race_number,
      horse: horseState.selectedHorse?.name,
    });
  };

  // Handle race selection
  setRaceNumbersList = (idx = 0) => {
    let { selectedRace, trackDateRaces, showRightPanel } = this.state;

    if (idx === null) {
      showRightPanel = false;
      idx = trackDateRaces.findIndex((x) => x.race_id === selectedRace.race_id);
      if (idx === -1 || idx >= trackDateRaces.length) {
        idx = 0;
      }
    }

    const selectedRaceData = trackDateRaces[idx];
    
    if (!selectedRaceData) {
      return;
    }

    const horseState = this.getFirstHorseState(selectedRaceData.horses || []);

    this.setState({
      ...horseState,
      selectedRace: selectedRaceData,
      showRightPanel: showRightPanel,
    });

    this.trackAnalyticsActions('race selected', {
      race: selectedRaceData.race_number,
      horse: horseState.selectedHorse?.name,
    });
  };

  // Handle horse selection
  setHorsesBox = (idx = 0) => {
    const { horses } = this.state;
    const selectedHorse = horses[idx];

    if (!selectedHorse) {
      return;
    }

    const horseWithSaddle = {
      ...selectedHorse,
      saddle: this.getSaddle(selectedHorse)
    };

    const selectedNote = horseWithSaddle.notes?.[0];
    const hasPrevHorse = idx > 0;
    const hasNextHorse = idx < horses.length - 1;

    this.setState({
      selectedHorse: horseWithSaddle,
      selectedNote: selectedNote,
      hasPrevHorse: hasPrevHorse,
      hasNextHorse: hasNextHorse,
      horseIndex: idx,
      showRightPanel: true,
    });

    this.trackAnalyticsActions('horse selected', {
      horse: horseWithSaddle.name
    });
  };

  // Navigate between horses
  onPrevNextHorse = (type) => {
    const { hasPrevHorse, hasNextHorse, horseIndex } = this.state;

    if (type === 'prev' && hasPrevHorse) {
      this.setHorsesBox(horseIndex - 1);
    } else if (type === 'next' && hasNextHorse) {
      this.setHorsesBox(horseIndex + 1);
    }
  };

  // Set selected note
  setNote = (idx) => {
    const { selectedHorse } = this.state;

    if (selectedHorse?.notes?.[idx]) {
      this.setState({
        selectedNote: selectedHorse.notes[idx]
      });
    }
  };

  // Extract common first-horse state shared by setTopLevelState, setTrackDatesList, setRaceNumbersList
  getFirstHorseState = (horses) => {
    let selectedHorse = horses[0] || null;
    if (selectedHorse) {
      selectedHorse = {
        ...selectedHorse,
        saddle: this.getSaddle(selectedHorse),
      };
    }
    return {
      horses,
      selectedHorse,
      selectedNote: selectedHorse?.notes?.[0],
      hasPrevHorse: false,
      hasNextHorse: horses.length > 1,
    };
  };

  // Get saddle color for horse
  getSaddle = (selectedHorse) => {
    if (!selectedHorse) {
      return {};
    }

    const { horseNumber } = this.getHorseNumber(
      selectedHorse.number,
      selectedHorse.number_str
    );
    
    return saddles.find((x) => Number(x.number) === Number(horseNumber)) || {};
  };

  // Parse horse number
  getHorseNumber = (number, numberStr) => {
    const horseNumDisplay = String(numberStr || number);
    const horseNumber = parseInt(horseNumDisplay.replace(/\D/g, ''), 10) || 0;
    
    return { horseNumDisplay, horseNumber };
  };

  // Handle language change
  onLanguageChange = async (language) => {
    if (!language || !isStirng(language)) {
      return;
    }

    try {
      const result = await this.props.updateUserProfile({ language });

      if (!this._isMounted) return;

      if (result) {
        this.loadData();
      }
    } catch (error) {
      console.error('Error changing language:', error);
      if (this._isMounted) {
        this.setState({ error: 'Failed to change language. Please try again.' });
      }
    }
  };

  // Navigate to pricing page
  onPurchaseClick = () => {
    this.props.history.push('/pricing');
  };

  // Track analytics
  trackAnalyticsActions = (action, props = {}) => {
    if (!action) {
      return;
    }

    const analyticsData = {
      user: this.props.auth?.user?.email,
      track: props.track || this.state.selectedTrack,
      date: props.date || this.state.selectedRaceDate,
      race: props.race || this.state.selectedRace?.race_number,
      horse: props.horse,
    };

    trackActivity(`dashboard: ${action}`, analyticsData);
  };

  render() {
    const { user, userProfile } = this.props.auth;

    const {
      raceData,
      sampleUser,
      selectedTrack,
      trackDates,
      trackDateRaces,
      selectedRaceDate,
      selectedRace,
      horses,
      selectedHorse,
      selectedNote,
      hasPrevHorse,
      hasNextHorse,
      showRightPanel,
      error,
      isLoadingData,
    } = this.state;

    const loading = this.props.auth.loading || isLoadingData;

    // Show error state
    if (error) {
      return (
        <div className="dashboard-body">
          <div className="error-container" style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Error Loading Dashboard</h2>
            <p>{error}</p>
            <button 
              className="btn primary" 
              onClick={() => {
                this.setState({ error: null });
                this.loadData();
              }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    // Show loading state
    if (loading) {
      return <Loading />;
    }

    // Empty state: user has no purchased races/packages yet
    if (!raceData?.[0]) {
      return (
        <div className="dashboard-body">
          <div className="error-container" style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>No purchased races yet</h2>
            <p>Your purchased races and packages will appear here.</p>
            <button className="btn primary" onClick={this.onPurchaseClick}>
              Buy Notes
            </button>
          </div>
        </div>
      );
    }

    const currentLanguage = userProfile?.language || 'en';

    return (
      <div className="dashboard-body">
        <div className="main-wrapper">
          <div className="header-block">
            <div className="navbar-dashboard w-nav">
              <div className="nav-inner">
                <a className="brand-logo w-nav-brand">
                  <img src={images['triplogo']} alt="Logo" />
                </a>

                <div className="dropdown-list-wrap">
                  <RaceDateDropDowns
                    data={raceData}
                    field="track"
                    selectedItem={selectedTrack}
                    handler={this.setTopLevelState}
                  />

                  <div className="dropdown-list-point"></div>

                  <RaceDateDropDowns
                    data={trackDates}
                    field="date"
                    selectedItem={selectedRaceDate}
                    handler={this.setTrackDatesList}
                  />
                </div>

                <div className="nav-top-menubar">
                  <button 
                    type="button" 
                    className="nav-link w-nav-link ctm" 
                    onClick={this.onPurchaseClick}
                  >
                    Buy Notes
                  </button>
                </div>

                <ProfileDropDown
                  user={sampleUser || user}
                  propsParent={this.props}
                />

                {currentLanguage && languages.length > 0 && languages.map((item) => {
                  if (currentLanguage !== item.id) {
                    return (
                      <div
                        className="language-block"
                        key={item.id}
                        onClick={() => this.onLanguageChange(item.id)}
                      >
                        <a>{item.value}</a>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-body">
              <div className="content-leftside">
                {/* Horizontal race numbers */}
                <div className="race-tab-wrapper">
                  {trackDateRaces?.length > 0 && trackDateRaces.map((item, idx) => (
                    <div
                      key={`${item.race_id}-${item.race_number}`}
                      className={classNames('racing-tab', {
                        current: selectedRace?.race_number === item?.race_number
                      })}
                      onClick={() => this.setRaceNumbersList(idx)}
                    >
                      RACE {item.race_number}
                    </div>
                  ))}
                </div>

                {/* Horse list */}
                <div className="race-content">
                  <div className="race-box-wp">
                    {horses.length > 0 && horses.map((item, idx) => {
                      const { horseNumDisplay, horseNumber } = this.getHorseNumber(
                        item.number,
                        item.number_str
                      );
                      const saddle = saddles.find(
                        (x) => Number(x.number) === horseNumber
                      ) || {};

                      // Create immutable copy instead of mutating
                      const horseData = {
                        ...item,
                        number: horseNumDisplay
                      };

                      return (
                        <HorseItem
                          key={item.id}
                          item={horseData}
                          saddle={saddle}
                          selectedHorse={selectedHorse}
                          grade={item?.notes?.[0]?.grade || 0}
                          onSetHorsesBox={() => this.setHorsesBox(idx)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right panel with horse details */}
              <div 
                className="content-rightbar" 
                style={{ visibility: showRightPanel ? 'visible' : 'hidden' }}
              >
                <div
                  className="rightbar-head-block"
                  style={{
                    backgroundColor: selectedHorse?.saddle?.color,
                    color: selectedHorse?.saddle?.color_2
                  }}
                >
                  <h3 className="rightbar-head-text">
                    {selectedHorse?.number} {selectedHorse?.name}
                  </h3>

                  <div 
                    className="close-btn" 
                    onClick={() => this.setRaceNumbersList(null)}
                  >
                    <img src={images['closebk'].default} alt="Close" />
                  </div>
                </div>

                <Notes
                  notes={selectedHorse?.notes}
                  selectedNote={selectedNote || null}
                  onNotesClick={this.setNote}
                />

                <div className="rightbar-button">
                  <button
                    type="button"
                    disabled={!hasPrevHorse}
                    className="btn primary"
                    onClick={() => this.onPrevNextHorse('prev')}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!hasNextHorse}
                    className="btn primary"
                    onClick={() => this.onPrevNextHorse('next')}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/*
Dashboard.propTypes = {
  auth: PropTypes.object.isRequired,
  message: PropTypes.object.isRequired,
  dashboard: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  getUserProfile: PropTypes.func.isRequired,
  getRaceData: PropTypes.func.isRequired,
  updateUserProfile: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  getUserProfileOverRide: PropTypes.func,
  getRaceDataOverRide: PropTypes.func,
  getRaceIdxToHighlight: PropTypes.func,
};
*/


const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  logout,
  getUserProfile,
  updateUserProfile
})(Dashboard);

//export default Dashboard;